/**
 * Hand-maintained types matching supabase/migrations/*_booking_portal_schema.sql.
 * Regenerate from Supabase CLI later if desired: `supabase gen types typescript`.
 */

export type BookingCategory =
  | "care_home"
  | "private_event"
  | "wedding"
  | "corporate"
  | "other";

export type BookingStatus = "scheduled" | "cancelled" | "archived";

export type PaymentStatus = "awaiting" | "paid" | "attention" | "not_required";

export type PaymentMethod = "stripe" | "bank_transfer" | "cash" | "other";

export type PaymentRecordStatus =
  | "pending"
  | "succeeded"
  | "failed"
  | "refunded"
  | "cancelled";

export type EmailKind =
  | "payment_request"
  | "payment_reminder_7d"
  | "olivia_alert_2d"
  | "payment_confirmed_client"
  | "payment_confirmed_olivia"
  | "other";

export type EmailDeliveryStatus = "queued" | "sent" | "failed" | "skipped";

export type ReminderKind = "client_7d" | "olivia_2d";

export type ReminderRunStatus = "sent" | "skipped" | "failed";

export type AdminProfile = {
  id: string;
  email: string;
  display_name: string | null;
  created_at: string;
  updated_at: string;
};

export type AppSettings = {
  id: 1;
  notification_email: string | null;
  default_due_days_before: number;
  reminder_7d_enabled: boolean;
  alert_2d_enabled: boolean;
  email_sign_off: string | null;
  created_at: string;
  updated_at: string;
};

export type Client = {
  id: string;
  venue_or_client_name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
};

export type Booking = {
  id: string;
  reference: string;
  client_id: string;
  group_id?: string | null;
  venue_postcode?: string | null;
  distance_miles?: number | null;
  fuel_charge_pence?: number;
  performance_date: string;
  start_time: string | null;
  end_time: string | null;
  duration_minutes: number | null;
  address: string | null;
  category: BookingCategory;
  internal_notes: string | null;
  client_description: string | null;
  extra_email?: string | null;
  /** GBP minor units */
  fee_pence: number;
  currency: "gbp";
  due_date: string | null;
  preferred_payment_method: PaymentMethod | null;
  booking_status: BookingStatus;
  payment_status: PaymentStatus;
  pay_token: string;
  cancelled_at: string | null;
  cancel_reason: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
};

export type Payment = {
  id: string;
  booking_id: string;
  amount_pence: number;
  currency: "gbp";
  method: PaymentMethod;
  status: PaymentRecordStatus;
  stripe_checkout_session_id: string | null;
  stripe_payment_intent_id: string | null;
  stripe_charge_id: string | null;
  paid_at: string | null;
  notes: string | null;
  recorded_by: string | null;
  created_at: string;
  updated_at: string;
};

export type EmailHistory = {
  id: string;
  booking_id: string | null;
  kind: EmailKind;
  to_email: string;
  subject: string | null;
  provider_message_id: string | null;
  status: EmailDeliveryStatus;
  error_message: string | null;
  payload: Record<string, unknown> | null;
  sent_at: string | null;
  created_at: string;
  created_by: string | null;
};

export type ReminderHistory = {
  id: string;
  booking_id: string;
  kind: ReminderKind;
  status: ReminderRunStatus;
  run_at: string;
  details: string | null;
  email_history_id: string | null;
  created_at: string;
};

export type StripeWebhookEvent = {
  id: string;
  stripe_event_id: string;
  type: string;
  payload: Record<string, unknown>;
  processed_at: string | null;
  processing_error: string | null;
  created_at: string;
};

export type AuditLog = {
  id: string;
  actor_id: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  meta: Record<string, unknown> | null;
  created_at: string;
};

/** Safe public pay-page projection from get_booking_pay_page() */
export type BookingPayPage = {
  reference: string;
  venue_or_client_name: string;
  contact_name?: string | null;
  client_email?: string | null;
  fuel_charge_pence?: number;
  distance_miles?: number | null;
  created_at?: string;
  performance_date: string;
  start_time: string | null;
  end_time: string | null;
  address: string | null;
  venue_postcode: string | null;
  fee_pence: number;
  currency: string;
  due_date: string | null;
  client_description: string | null;
  booking_status: BookingStatus;
  payment_status: PaymentStatus;
};

/** Format pence → £x.xx for UI (never store floats). */
export function formatGbpFromPence(pence: number): string {
  const sign = pence < 0 ? "-" : "";
  const abs = Math.abs(pence);
  const pounds = Math.floor(abs / 100);
  const pennies = abs % 100;
  return `${sign}£${pounds}.${pennies.toString().padStart(2, "0")}`;
}

export function poundsToPence(pounds: number): number {
  return Math.round(pounds * 100);
}

export type GroupPayRow = {
  contact_name?: string | null;
  client_email?: string | null;
  fuel_charge_pence?: number;
  distance_miles?: number | null;
  reference: string;
  venue_or_client_name: string;
  performance_date: string;
  start_time: string | null;
  end_time: string | null;
  address: string | null;
  venue_postcode: string | null;
  fee_pence: number;
  currency: string;
  due_date: string | null;
  booking_status: BookingStatus;
  payment_status: PaymentStatus;
  pay_token: string;
};
